import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, XCircle, AlertTriangle, ArrowUpRight, User, Clock } from 'lucide-react';
import { finalDecisionLabel } from '@/lib/enumLabels';
import type { FinalDecision, GateDecision } from '@/types';
import { cn } from '@/lib/utils';

export interface ReviewSummaryProps {
  reviewerName: string;
  reviewedAt: string;
  decisionType: 'accept' | 'override' | 'escalate';
  aiDecision: GateDecision;
  finalDecision: FinalDecision | null;
  justification: string;
  annotationsSummary: { agree: number; disagree: number; discuss: number };
  className?: string;
}

const DECISION_STYLE: Record<FinalDecision, { className: string; Icon: React.FC<{ className?: string }> }> = {
  APPROVED: {
    className: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
    Icon: CheckCircle,
  },
  APPROVED_WITH_ALERTS: {
    className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
    Icon: AlertTriangle,
  },
  REJECTED: {
    className: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
    Icon: XCircle,
  },
  ESCALATED: {
    className: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
    Icon: ArrowUpRight,
  },
};

/**
 * Read-only view of a completed review decision — shown after submission.
 * Displays reviewer name, decision badge, justification, and annotation summary.
 */
export function ReviewSummary({
  reviewerName,
  reviewedAt,
  decisionType,
  aiDecision,
  finalDecision,
  justification,
  annotationsSummary,
  className,
}: ReviewSummaryProps) {
  // For display: if no human decision yet, fall back to ESCALATED style as safe default.
  const decision: FinalDecision = finalDecision ?? 'ESCALATED';
  const cfg = DECISION_STYLE[decision];
  const { Icon } = cfg;

  const formattedDate = new Date(reviewedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const isOverride = decisionType === 'override' && finalDecision !== aiDecision;
  const totalAnnotations =
    annotationsSummary.agree + annotationsSummary.disagree + annotationsSummary.discuss;

  return (
    <Card className={cn('border-2', decision === 'APPROVED' ? 'border-green-300 dark:border-green-700' : decision === 'REJECTED' ? 'border-red-300 dark:border-red-700' : 'border-purple-300 dark:border-purple-700', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          Review Complete
          <Badge variant="outline" className={cn('text-xs', cfg.className)}>
            <Icon className="h-3 w-3 mr-1" aria-hidden="true" />
            {finalDecisionLabel[decision]}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">

        {/* Reviewer + time */}
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <User className="h-3.5 w-3.5" aria-hidden="true" />
            {reviewerName}
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {formattedDate}
          </span>
        </div>

        {/* Override note */}
        {isOverride && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2">
            Overridden from AI recommendation: <strong>{aiDecision}</strong>
        </div>
        )}

        {/* Justification */}
        {justification && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Justification</p>
              <p className="text-sm text-foreground">{justification}</p>
            </div>
          </>
        )}

        {/* Annotation summary */}
        {totalAnnotations > 0 && (
          <>
            <Separator />
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Annotation Summary ({totalAnnotations} finding{totalAnnotations !== 1 ? 's' : ''} annotated)
              </p>
              <div className="flex flex-wrap gap-3 text-xs">
                {annotationsSummary.agree > 0 && (
                  <span className="flex items-center gap-1 text-green-700 dark:text-green-400">
                    <CheckCircle className="h-3 w-3" aria-hidden="true" />
                    {annotationsSummary.agree} agreed
                  </span>
                )}
                {annotationsSummary.disagree > 0 && (
                  <span className="flex items-center gap-1 text-red-700 dark:text-red-400">
                    <XCircle className="h-3 w-3" aria-hidden="true" />
                    {annotationsSummary.disagree} disagreed
                  </span>
                )}
                {annotationsSummary.discuss > 0 && (
                  <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    {annotationsSummary.discuss} need discussion
                  </span>
                )}
              </div>
            </div>
          </>
        )}

      </CardContent>
    </Card>
  );
}
